import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  TrendingUp,
  Settings,
  Lock,
  LogOut,
  Network,
  X,
  CreditCard,
} from "lucide-react";
import { useThemeStore } from "../../store/themeStore";

interface SidebarProps {
  isCollapsed: boolean;
  isMobileMenuOpen: boolean;
  setMobileMenuOpen: (isOpen: boolean) => void;
}

const menuItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/hotels", label: "Hotels", icon: Building2 },
  { path: "/admins", label: "Admins", icon: Users },
  { path: "/performance", label: "Performance", icon: TrendingUp },
  { path: "/settings", label: "Settings", icon: Settings },
  { path: "/reset-password", label: "Reset Password", icon: Lock },
  { path: "/channel-manager", label: "Channel Manager", icon: Network },
  { path: "/subscription", label: "Subscription", icon: CreditCard },
];

const Sidebar = ({
  isCollapsed,
  isMobileMenuOpen,
  setMobileMenuOpen,
}: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("OneDeskToken");
    navigate("/desklogin");
  };

  const { theme, toggleTheme } = useThemeStore();

  const currentPath = location.pathname;

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white dark:bg-[#111c44] text-slate-700 dark:text-slate-200 overflow-hidden shadow-sm transition-colors duration-300">
      {/* Logo Section */}
      <div className="h-[70px] flex items-center px-6 border-b border-slate-100 dark:border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          {/* <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center shrink-0 shadow-md shadow-blue-500/60">
            <span className="text-white font-bold text-xl leading-none">O</span>
          </div> */}
          <div
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center shrink-0"
            style={{ boxShadow: "0 8px 25px rgba(59,130,246,0.25)" }}
          >
            <span className="text-white font-bold text-xl leading-none">O</span>
          </div>
          <div
            className={`flex flex-col transition-opacity duration-300 ${
              isCollapsed && !isMobileMenuOpen
                ? "opacity-0 w-0 hidden"
                : "opacity-100"
            }`}
          >
            <span className="font-bold text-xl leading-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              OneDesk
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-4 space-y-1 overflow-y-auto custom-scrollbar overflow-x-hidden">
        {menuItems
          .filter((item) => item.label !== "Settings")
          .map((item) => {
            const isActive = currentPath.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                title={
                  isCollapsed && !isMobileMenuOpen ? item.label : undefined
                }
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative ${
                  isActive
                    ? "bg-[#EFF6FF] dark:bg-[#1b254b] text-[#2563EB] dark:text-white font-semibold"
                    : "text-[#64748B] dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 hover:text-slate-800 dark:hover:text-slate-200"
                }`}
              >
                <item.icon
                  size={20}
                  className={`transition-colors shrink-0 ${
                    isActive
                      ? "text-[#2563EB] dark:text-white"
                      : "text-[#94A3B8] dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300"
                  } ${isCollapsed && !isMobileMenuOpen ? "mx-auto" : ""}`}
                />

                <div
                  className={`flex-1 flex items-center justify-between overflow-hidden transition-all duration-300 ${
                    isCollapsed && !isMobileMenuOpen
                      ? "w-0 opacity-0 hidden"
                      : "w-auto opacity-100"
                  }`}
                >
                  <span className={`text-[13px] whitespace-nowrap`}>
                    {item.label}
                  </span>

                  {isActive && (
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] dark:bg-white shrink-0" />
                  )}
                </div>
              </Link>
            );
          })}
      </nav>

      {/* Bottom Section */}
      <div className="p-4 shrink-0 space-y-2 border-t border-slate-50 dark:border-white/10">
        {/* Settings Item specifically */}
        {menuItems
          .filter((item) => item.label === "Settings")
          .map((item) => {
            const isActive = currentPath.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                title={
                  isCollapsed && !isMobileMenuOpen ? item.label : undefined
                }
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative mb-2 ${
                  isActive
                    ? "bg-[#EFF6FF] dark:bg-[#1b254b] text-[#2563EB] dark:text-white font-semibold"
                    : "text-[#64748B] dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 hover:text-slate-800 dark:hover:text-slate-200"
                }`}
              >
                <item.icon
                  size={20}
                  className={`transition-colors shrink-0 ${
                    isActive
                      ? "text-[#2563EB] dark:text-blue-400"
                      : "text-[#94A3B8] dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300"
                  } ${isCollapsed && !isMobileMenuOpen ? "mx-auto" : ""}`}
                />

                <div
                  className={`flex-1 flex items-center justify-between overflow-hidden transition-all duration-300 ${
                    isCollapsed && !isMobileMenuOpen
                      ? "w-0 opacity-0 hidden"
                      : "w-auto opacity-100"
                  }`}
                >
                  <span className={`text-[13px] whitespace-nowrap`}>
                    {item.label}
                  </span>

                  {isActive && (
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] dark:bg-blue-400 shrink-0" />
                  )}
                </div>
              </Link>
            );
          })}
        <div
          className={`flex items-center gap-3 p-3.5 rounded-[22px] transition-all duration-300 ${
            isCollapsed && !isMobileMenuOpen
              ? "justify-center bg-transparent border-transparent"
              : "bg-[#F8FAFC] dark:bg-[#1b254b]/50 border border-slate-100 dark:border-white/5"
          }`}
        >
          <div className="w-10 h-10 rounded-xl bg-[#2563EB] dark:bg-blue-600 flex items-center justify-center shrink-0 shadow-lg shadow-blue-100 dark:shadow-blue-900/20 uppercase">
            <span className="text-white font-bold text-lg">S</span>
          </div>

          <div
            className={`flex flex-col min-w-0 transition-opacity duration-300 ${
              isCollapsed && !isMobileMenuOpen ? "hidden" : "opacity-100"
            }`}
          >
            <span className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">
              Super Admin
            </span>
            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-tighter">
              Master Node
            </span>
          </div>
        </div>

        {/* Theme Toggle - Mobile Only */}
        <div className="lg:hidden flex items-center justify-between px-3 py-2 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50">
          <span className="text-sm font-medium">Theme</span>

          <button
            onClick={toggleTheme}
            className={`flex items-center p-1 rounded-full w-12 h-6 relative ${
              theme === "dark" ? "bg-indigo-600" : "bg-slate-300"
            }`}
          >
            <div
              className={`w-4 h-4 bg-white rounded-full transition-transform ${
                theme === "dark" ? "translate-x-6" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        <button
          onClick={handleLogout}
          title={isCollapsed && !isMobileMenuOpen ? "Logout" : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:text-rose-600 dark:hover:text-rose-400 transition-colors group overflow-hidden ${
            isCollapsed && !isMobileMenuOpen ? "justify-center" : ""
          }`}
        >
          <LogOut
            size={18}
            className="shrink-0 group-hover:-translate-x-0.5 transition-transform"
          />
          <span
            className={`font-bold text-xs whitespace-nowrap transition-all duration-300 ${
              isCollapsed && !isMobileMenuOpen
                ? "w-0 opacity-0 hidden"
                : "w-auto opacity-100"
            }`}
          >
            Logout
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 lg:hidden transition-opacity"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-72 z-50 lg:hidden transform transition-transform duration-300 ease-in-out shadow-2xl ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="absolute top-4 right-4 p-2 z-10 text-slate-400 hover:text-slate-800 bg-white rounded-full shadow-sm lg:hidden border border-slate-100"
        >
          <X size={20} />
        </button>
        <SidebarContent />
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed top-0 left-0 bottom-0 z-50 transition-all duration-300 ease-in-out bg-white dark:bg-[#111c44] border-r border-slate-200 dark:border-white/10 ${
          isCollapsed ? "w-20" : "w-[260px]"
        }`}
      >
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
